// src/components/theme/NewThemePrompt.js
//
// One-time offer of the Sky light theme to people whose light theme is still
// the older cream one. Shows once, then never again whichever button is
// pressed -- dismissing is an answer, not a postponement.
//
// Delete this component (and its mount in App.js) once the release it shipped
// in is far enough back that everyone has seen it.

import React, { useEffect, useState } from 'react';
import { Modal, Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { getThemePreferences, selectTheme } from '../../utils/themeUtils';

const SEEN_KEY = 'skyThemePromptSeen';
const OLD_LIGHT_THEME = 'comfort-light';
const NEW_LIGHT_THEME = 'classic-light';

const NewThemePrompt = () => {
  const { t } = useTranslation('common');
  const [show, setShow] = useState(false);

  useEffect(() => {
    let seen = null;
    try { seen = localStorage.getItem(SEEN_KEY); } catch (e) { return; }
    if (seen === 'true') return;

    // Only ask people it's actually about. Anyone already on Sky, or who has
    // never used a light theme, gets nothing.
    if (getThemePreferences().lightTheme !== OLD_LIGHT_THEME) return;

    setShow(true);
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(SEEN_KEY, 'true'); } catch (e) { /* private mode */ }
    setShow(false);
  };

  const accept = () => {
    selectTheme(NEW_LIGHT_THEME);
    dismiss();
  };

  if (!show) return null;

  return (
    <Modal show onHide={dismiss} centered className="new-theme-prompt">
      <Modal.Body className="new-theme-prompt-body">
        <h5 className="new-theme-prompt-title">
          {t('common:newLightThemeTitle', 'A new light theme')}
        </h5>
        <p className="new-theme-prompt-text">
          {t(
            'common:newLightThemeBody',
            'Sky matches the look of govglance.org. Your current light theme stays available in Preferences.'
          )}
        </p>

        <div className="new-theme-prompt-previews">
          <div className="new-theme-prompt-preview">
            <div
              className="new-theme-prompt-swatch"
              style={{ background: 'linear-gradient(180deg, #f0f7ff 0%, #dbeafe 100%)' }}
            >
              <div className="new-theme-prompt-card" style={{ backgroundColor: '#ffffff' }}>
                <div className="new-theme-prompt-bar" style={{ backgroundColor: '#2563eb' }} />
              </div>
            </div>
            <span>{t('common:themeSky', 'Sky')}</span>
          </div>

          <div className="new-theme-prompt-preview">
            <div className="new-theme-prompt-swatch" style={{ backgroundColor: '#F5F3EF' }}>
              <div className="new-theme-prompt-card" style={{ backgroundColor: '#FFFEFA' }}>
                <div className="new-theme-prompt-bar" style={{ backgroundColor: '#016cae' }} />
              </div>
            </div>
            <span>{t('common:themeClean', 'Clean')}</span>
          </div>
        </div>

        <div className="new-theme-prompt-actions">
          <Button className="new-theme-prompt-primary" onClick={accept}>
            {t('common:tryNewTheme', 'Use Sky')}
          </Button>
          <Button className="new-theme-prompt-secondary" onClick={dismiss}>
            {t('common:keepCurrentTheme', 'Keep current')}
          </Button>
        </div>
      </Modal.Body>

      <style>{`
        .new-theme-prompt .modal-content {
          background-color: var(--bg-card);
          border: 1px solid var(--border-primary);
          border-radius: 16px;
        }
        .new-theme-prompt-body { padding: 24px; }
        .new-theme-prompt-title {
          margin: 0 0 8px 0;
          font-size: 18px;
          font-weight: 600;
          color: var(--text-primary);
        }
        .new-theme-prompt-text {
          margin: 0 0 20px 0;
          font-size: 14px;
          line-height: 1.5;
          color: var(--text-secondary);
        }
        .new-theme-prompt-previews {
          display: flex;
          gap: 16px;
          margin-bottom: 22px;
        }
        .new-theme-prompt-preview {
          flex: 1;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          font-size: 13px;
          color: var(--text-secondary);
        }
        .new-theme-prompt-swatch {
          width: 100%;
          height: 56px;
          border-radius: 10px;
          border: 1px solid var(--border-primary);
          display: flex;
          align-items: center;
          justify-content: center;
          overflow: hidden;
        }
        .new-theme-prompt-card {
          width: 52px;
          height: 34px;
          border-radius: 5px;
          overflow: hidden;
        }
        .new-theme-prompt-bar { height: 8px; width: 100%; }
        .new-theme-prompt-actions { display: flex; gap: 10px; }
        .new-theme-prompt-primary,
        .new-theme-prompt-secondary {
          flex: 1;
          border-radius: 24px;
          font-size: 14px;
          padding: 10px;
          border: none;
        }
        .new-theme-prompt-primary {
          background-color: var(--accent-primary);
          color: #ffffff;
        }
        .new-theme-prompt-secondary {
          background-color: var(--bg-button-secondary);
          color: var(--text-primary);
          border: 1px solid var(--border-primary);
        }
      `}</style>
    </Modal>
  );
};

export default NewThemePrompt;